import {
  currencyFormatter,
  Designation,
  LeadDTO,
  User,
  Vehicle,
  VehicleGetDTO,
  VehicleSort,
} from "@alba-cars/common-modules";
import { InfiniteData } from "@tanstack/react-query";
import { clsx, type ClassValue } from "clsx";
import { redirect } from "next/navigation";
import { twMerge } from "tailwind-merge";

import {
  ExtendedVehicleFilter,
  FilterArrayField,
  FilterRangeFields,
  OmittedVehicleGetDTO,
} from "@/app/[locale]/buy-used-cars-uae/_components/types";
import { DESIGNATION_TRANSLATIONS, footerMakeMap, NavigationKeys } from "@/utils/constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export function convertTo24Hour(time: string): string {
  // Extract hours, minutes, and period
  const [timeStr, period] = time.split(" ");
  let [hours, minutes] = timeStr.split(":").map(Number);

  // Convert to 24-hour format
  if (period.toLowerCase() === "pm" && hours !== 12) {
    hours += 12;
  } else if (period.toLowerCase() === "am" && hours === 12) {
    hours = 0;
  }

  // Format the output with leading zeros
  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}`;
}

/**
 * Validates if a string is a valid URL
 */
function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

const shouldAddAutoFormat = (hostOrDomain: string | undefined, addAutoFormat?: boolean): boolean => {
  if (!hostOrDomain) {
    return false;
  }

  return (
    hostOrDomain.includes("cloudfront") || (hostOrDomain.includes("storage.albacars.ae") && Boolean(addAutoFormat))
  );
};

const appendAutoFormatParam = (url: string): string => {
  if (url.includes("format=auto")) {
    return url;
  }

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}format=auto`;
};

/**
 * Formats an image URL by ensuring it has the correct domain and leading slash
 * @param src - The source URL or path
 * @param config - Optional configuration object
 * @returns The properly formatted URL
 * @throws Error if src is undefined or null
 */
export function formatImageUrl(src: string | null | undefined, options?: { addAutoFormat?: boolean }): string | null {
  if (!src) {
    return null;
  }

  // Return as-is if it's a data URL
  if (src.startsWith("data:")) {
    return src;
  }
  if (src.startsWith("/assets")) {
    return src;
  }

  const domain = process.env.NEXT_PUBLIC_S3_URL;

  // Absolute URLs from our CDN may still need ?format=auto
  if (isValidUrl(src)) {
    if (shouldAddAutoFormat(src, options?.addAutoFormat)) {
      return appendAutoFormatParam(src);
    }

    return src;
  }

  // Ensure src has a leading slash
  const normalizedSrc = src.startsWith("/") ? src : `/${src}`;

  if (shouldAddAutoFormat(domain, options?.addAutoFormat)) {
    return appendAutoFormatParam(`${domain}${normalizedSrc}`);
  }

  // Combine domain with normalized path
  return `${domain}${normalizedSrc}`;
}

export const getPublicFileUrl = (fileName: string) => {
  if (!fileName) return "";
  return `${process.env.NEXT_PUBLIC_S3_URL}/${fileName}`;
};

export const constructInfiniteQueryData = <T>(data: T, page?: number): InfiniteData<T> => {
  const result: InfiniteData<T> = {
    pages: [data],
    pageParams: [page ?? 0],
  };

  return result;
};

export const baseQuery: OmittedVehicleGetDTO = {
  options: {
    page: 1,
    limit: 20,
    withMake: true,
    withModel: true,
    withBodyType: true,
    withFinance: true,
    select: [
      "id",
      "refId",
      "title",
      "vin",
      "status",
      "ownershipType",
      "chassisNumber",
      "isPinned",
      "isBrandNew",
      "isOnSale",
      "trim",
      "color",
      "airbags",
      "doors",
      "price",
      "year",
      "fuelType",
      "engineCapacity",
      "useCustomTitle",
      "lastPublishedAt",
      "mileage",
      "isPinned",
      "isBrandNew",
      "createdAt",
      "updatedAt",
      "make.id",
      "make.name",
      "model.id",
      "model.name",
      "bodyType.id",
      "bodyType.name",
    ],
  },
};

export const ARRAY_FIELDS: FilterArrayField[] = [
  "make",
  "model",
  "bodyType",
  "fuelType",
  "features",
  "color",
  "locationPresetSlug",
];
export const RANGE_FIELDS: FilterRangeFields[] = ["mileage", "year", "price", "emi"];
const EXTRA_ALLOWED_FILTERS = [
  "chassisNumber",
  "stockAgeDays",
  "location",
  "showroomLocation",
  "locationType",
];
const LOCATION_TYPE_VALUES = ["Showroom", "External"] as const;

const parseShowroomLocationParam = (getParam: (key: string) => unknown): string | undefined => {
  const value = getParam("location") ?? getParam("showroomLocation");
  if (!value) return undefined;
  const slug = Array.isArray(value) ? value[0] : value;
  return slug ? String(slug) : undefined;
};

const parseLocationTypeParam = (
  getParam: (key: string) => unknown,
): (typeof LOCATION_TYPE_VALUES)[number] | undefined => {
  const value = getParam("locationType");
  if (!value) return undefined;
  const locationType = Array.isArray(value) ? value[0] : value;
  if (
    typeof locationType === "string" &&
    LOCATION_TYPE_VALUES.includes(locationType as (typeof LOCATION_TYPE_VALUES)[number])
  ) {
    return locationType as (typeof LOCATION_TYPE_VALUES)[number];
  }
  return undefined;
};
export const genQuery = async (
  searchParams: Record<string, any>,
): Promise<Omit<VehicleGetDTO, "toPlain" | "validate">> => {
  // Check for filter parameters
  const hasFilterParams = Object.keys(searchParams).some(
    (key) =>
      key.endsWith("Min") ||
      key.endsWith("Max") ||
      ARRAY_FIELDS.includes(key as FilterArrayField) ||
      key === "makes" ||
      key === "isBrandNew" ||
      key === "isSignature" ||
      key === "isOnSale" ||
      EXTRA_ALLOWED_FILTERS.includes(key),
  );

  // Initialize the query with base query
  const query = { ...baseQuery };

  // Handle filters if present
  if (hasFilterParams) {
    const initialFilter = {} as ExtendedVehicleFilter;

    const getParam = (key: string) => {
      if (searchParams instanceof URLSearchParams) {
        return searchParams.get(key);
      }
      return searchParams[key];
    };

    // Handle array fields
    ARRAY_FIELDS.forEach((field) => {
      if (field === "odometer") {
        return;
      }
      if (searchParams[field]) {
        const value = getParam(field);
        initialFilter[field] = Array.isArray(value) ? value : [value];
      }
    });

    const minPrice = getParam("minPrice") || getParam("minprice");
    const maxPrice = getParam("maxPrice") || getParam("maxprice");

    // Set price range if either parameter is present
    if (minPrice || maxPrice) {
      initialFilter.price = {
        min: minPrice ? Number(minPrice) : undefined,
        max: maxPrice ? Number(maxPrice) : undefined,
      };
    }

    const chassisNumber = getParam("chassisNumber");
    if (chassisNumber) {
      initialFilter.chassisNumber = chassisNumber;
    }

    const showroomLocation = parseShowroomLocationParam(getParam);
    if (showroomLocation) {
      initialFilter.showroomLocation = showroomLocation;
    }

    const locationType = parseLocationTypeParam(getParam);
    if (locationType) {
      initialFilter.locationType = locationType;
    }

    const stockAgeDays = getParam("stockAgeDays");
    if (stockAgeDays) {
      const parsedStockAgeDays = Number(stockAgeDays);
      if (!Number.isNaN(parsedStockAgeDays)) {
        initialFilter.stockAgeDays = parsedStockAgeDays;
      }
    }

    const isBrandNew = getParam("isBrandNew");
    if (isBrandNew === "true") {
      initialFilter.isBrandNew = "true";
    }

    const isSignature = getParam("isSignature");
    if (isSignature === "true") {
      initialFilter.isSignature = "true";
    }

    const isOnSale = getParam("isOnSale");
    if (isOnSale === "true") {
      initialFilter.isOnSale = "true";
    }
    const odometer = getParam("odometer");

    if (odometer) {
      initialFilter.mileage = {
        min: odometer ? Number(odometer) : undefined,
        max: odometer ? Number(odometer) : undefined,
      };
    }

    // Handle range fields
    RANGE_FIELDS.forEach((field) => {
      if (field === "price" && initialFilter.price) {
        return;
      }

      if (field === "mileage" && initialFilter.mileage) {
        return;
      }

      const min = getParam(`${field}Min`);
      const max = getParam(`${field}Max`);
      if (min || max) {
        initialFilter[field] = {
          min: min ? Number(min) : undefined,
          max: max ? Number(max) : undefined,
        };
      }
    });

    if (getParam("makes")) {
      const makeSlug = footerMakeMap[getParam("makes") as keyof typeof footerMakeMap];
      if (makeSlug) {
        redirect(`/search?make=${makeSlug}&source=${NavigationKeys.FOOTER}`);
      } else {
        redirect(`/search`);
      }
    }

    if (Object.keys(initialFilter).length > 0) {
      query.filter = initialFilter;
    }
  }

  const sort: Record<string, "asc" | "desc" | Record<string, "asc" | "desc">> = {};
  Array.from(Object.entries(searchParams)).forEach(([key, value]) => {
    if (key.startsWith("sort[")) {
      const matches = key.match(/sort\[(.*?)\](?:\[(.*?)\])?/);
      if (matches) {
        const [, mainKey, nestedKey] = matches;

        if (nestedKey) {
          // Handle nested sort
          if (!sort[mainKey] || typeof sort[mainKey] === "string") {
            sort[mainKey] = {};
          }
          (sort[mainKey] as Record<string, "asc" | "desc">)[nestedKey] = value as "asc" | "desc";
        } else {
          // Handle simple sort
          sort[mainKey] = value as "asc" | "desc";
        }
      }
    }
  });
  // Only set options if there are any

  query.options = {
    ...baseQuery.options,
    sort: sort as VehicleSort | undefined,
  };

  return query;
};

// Simulate a delayed response
export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Car favorites
export const FAVORITES_KEY = "car-favorites";

export const getLocalFavorites = (): Set<string> => {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    return new Set(stored ? JSON.parse(stored) : []);
  } catch {
    return new Set();
  }
};

export const saveLocalFavorites = (favorites: Set<string>): void => {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(Array.from(favorites)));
};

export interface Deposit {
  id: string;
  stripePaymentIntentId: string;
  amount: number;
  currency: string;
  status: "pending" | "completed" | "failed";
  isRefundable: boolean;
  isRefunded: boolean;
  lead?: LeadDTO;
  user?: User;
  vehicle: Vehicle;
  isDeleted?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export const getFileSizeInfo = (size: number) => {
  return {
    bytes: size,
    kilobytes: size / 1024,
    megabytes: size / 1024 / 1024,
    gigabytes: size / 1024 / 1024 / 1024,
  };
};

// parses and returns the refId from the slug in the format refId-make-model
export const getVehicleRefFromSlug = (slugString: string) => {
  const [refId, _make, _model, _trim] = slugString.split("-");
  return refId;
};

export function slugify(text: string): string {
  if (!text || typeof text !== "string") return "";

  // Convert to lowercase and trim whitespace
  let slug = text.toLowerCase().trim();

  // Replace multiple spaces/dashes/underscores with single dash
  slug = slug.replace(/[\s_-]+/g, "-");

  // Remove any characters that aren't alphanumeric or dashes
  slug = slug.replace(/[^a-zA-Z0-9-]/g, "");

  // Remove dashes from start and end
  slug = slug.replace(/^-+|-+$/g, "");

  return slug;
}

// A utility function to split the array into rows (chunks) of given size.
export const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
};

export const MAX_SAFE_INTEGER = 2147483647;

/**
 * Removes the trailing slash from a URL if it exists
 */
export const removeUrlTrailingSlash = (url: string): string => {
  if (!url) return "";
  return url.endsWith("/") ? url.slice(0, -1) : url;
};

export const siteUrl = removeUrlTrailingSlash((process.env.NEXT_PUBLIC_APP_URL || "") as string);

export const getCarPrice = (car?: Vehicle): number => {
  if (!car) return 0;

  if (!car.finance) return 0;

  if (car.finance.vehiclePriceType === "dropped_price" && car.finance.droppedPrice) {
    return car.finance.droppedPrice;
  }

  if (car.finance.price) {
    return car.finance.price;
  }

  return 0;
};

export const getCarMonthlyInstallment = (car?: Vehicle): number => {
  if (!car) return 0;

  if (!car.finance) return 0;

  if (!car.finance.installmentAvailable) return 0;

  if (car.finance.vehiclePriceType === "dropped_price") {
    return car.finance.droppedMonthlyInstallment || car.finance.monthlyInstallment || 0;
  }

  if (car.finance.monthlyInstallment) {
    return car.finance.monthlyInstallment;
  }

  return 0;
};

export const roundNumberTo = (value: number, digits: number = 2) => {
  const ROUND = Math.pow(10, digits);
  return Math.round(value * ROUND) / ROUND;
};

interface SeedRandomOptions {
  shiftBits?: number; // Default: 5 (for hash << 5)
  modulo?: number; // Default: 4 (for hash % 4)
  offset?: number; // Default: 2 (for + 2)
}

/**
 * Generates a deterministic pseudo-random number from a seed string using a simple hash function.
 *
 * @param seedStr - The seed string to generate the number from. Same string always produces same result.
 * @param options - Optional configuration to control the output range and hash calculation
 * @param options.shiftBits - Number of bits to shift left during hash calculation.
 *                             Higher values create more variance in the hash. Default: 5
 * @param options.modulo - The divisor used to constrain the output range.
 *                         Result will be in range [0, modulo-1] before offset is applied. Default: 4
 * @param options.offset - Value added to the final result to shift the output range.
 *                         Final range is [offset, offset + modulo - 1]. Default: 2
 *
 * @returns A deterministic number in the range [offset, offset + modulo - 1].
 *          With defaults, returns a number between 2 and 5 (inclusive).
 *
 * @example
 * // Returns a number between 2-5
 * seedRandom("hello");
 *
 * @example
 * // Returns a number between 0-9
 * seedRandom("hello", { modulo: 10, offset: 0 });
 *
 * @example
 * // Returns a number between 1-100
 * seedRandom("hello", { modulo: 100, offset: 1 });
 */
export const seedRandom = (seedStr: string, options?: SeedRandomOptions): number => {
  const { shiftBits = 5, modulo = 4, offset = 2 } = options || {};

  let hash = 0;

  for (let i = 0; i < seedStr.length; i++) {
    const char = seedStr.charCodeAt(i);
    hash = (hash << shiftBits) - hash + char;
    hash = hash & hash;
  }

  hash = Math.abs(hash);

  return (hash % modulo) + offset;
};

/**
 * Returns a pluralized string based on the value
 * @example getPlural(1, 'item') => '1 item'
 * @example getPlural(4, 'item') => '4 items'
 * @example getPlural(2, 'person', 'people') => '2 people'
 * @example getPlural(0, 'item') => '0 items'
 */
export const getPlural = (value: number, singular: string, plural: string | null = null) =>
  value === 1 ? `${value} ${singular}` : plural ? `${value} ${plural}` : `${value} ${singular}s`;

type DistanceUnit = "miles" | "km";

interface ConvertDistanceOptions {
  from: DistanceUnit;
  to: DistanceUnit;
  noRounding?: boolean;
}

export const convertDistance = (value: number, options: ConvertDistanceOptions): number => {
  const { from, to, noRounding = false } = options;

  let result = value;

  if (from === to) {
    return result;
  }
  //* Conversion factor: 1 mile = 1.60934 kilometers
  const MILES_TO_KM = 1.60934;

  if (from === "miles" && to === "km") {
    result = value * MILES_TO_KM;
  } else {
    result = value / MILES_TO_KM;
  }

  return noRounding ? result : Math.round(result);
};

export const getLocalizedDesignation = (designation: Designation, locale: string): string => {
  switch (locale) {
    case "ar":
      return DESIGNATION_TRANSLATIONS[designation]["ar"];
    case "en":
    default:
      return designation.replace(/-/g, " ").replace(/(?:^|\s)\S/g, (match: string) => match.toUpperCase());
  }
};

export const getCookieDomain = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const hostname = window.location.hostname;

  // localhost or local IPs
  if (hostname === "localhost" || hostname.startsWith("127.") || hostname.endsWith(".local")) {
    return undefined;
  }

  // remove subdomain and keep root domain
  const parts = hostname.split(".");

  if (parts.length >= 2) {
    const rootDomain = parts.slice(-2).join(".");
    return `.${rootDomain}`;
  }

  return undefined;
};

export const formatPriceWithDirham = (amount: number): string => {
  return currencyFormatter({ isDecimal: true, decimalPlaces: 0 }).format(amount).replace("AED", "").trim();
};
